/**
 * Mapovanie React stránok → chosen_notifications.type (topic).
 * Backend: backend/app/Support/NotificationTopic.php (musí zostať v súlade).
 *
 * ---------------------------------------------------------------------------
 * PRAVIDLO (platí pre celý systém)
 * ---------------------------------------------------------------------------
 * Jeden kanonický topic zodpovedá jednému hlavnému zdroju dát – typicky jednej
 * databázovej tabuľke (alebo jednému API zdroju, ktorý reprezentuje tú istú entitu).
 * Všetky obrazovky, ktoré zobrazujú riadky z toho istého zdroja (rôzne filtre,
 * iné menu, portál vs. údržba), majú v PAGE_TO_TOPIC rovnaký topic.
 *
 * Ľudské názvy topicov: notificationTopicDisplayLabels.js (NOTIFICATION_TOPIC_DISPLAY_LABEL).
 * ---------------------------------------------------------------------------
 */

import { NOTIFICATION_TOPIC_DISPLAY_LABEL } from './notificationTopicDisplayLabels';

/** Firemný portál (service_tasks) – kľúče v sk/en/de.json pod `NotificationTopic.Portal.*`. */
const PORTAL_SERVICE_NOTIFICATION_LABEL_KEYS = Object.freeze({
  service_tasks: 'NotificationTopic.Portal.Tickets',
  'service_tasks.preview': 'NotificationTopic.Portal.Preview',
  'service_task.templates': 'NotificationTopic.Portal.TaskTemplates',
  'service_task.access_levels': 'NotificationTopic.Portal.AccessLevels',
  'service_task.states': 'NotificationTopic.Portal.States',
  'service_task.setup': 'NotificationTopic.Portal.ModuleSettings',
});

const DEFAULT_TOPIC = 'notification.default';

const PAGE_TO_TOPIC = {
  // --- maintenance_types ---
  MaintenanceTypeList: 'maintenance.types',

  // --- maintenance_scheduled_records (+ súvisiace) ---
  MaintenanceStateList: 'maintenance.scheduled_records',

  // --- maintenance_scheduled / karty strojov ---
  MaintenanceRecordList: 'maintenance.machine_cards',
  MaintenanceRecordDetail: 'maintenance.machine_cards',
  MaintenanceCardDetail: 'maintenance.machine_cards',
  MaintenanceCardList: 'maintenance.machine_cards',

  // --- service_tasks (jedna tabuľka: firemný portál + údržba) ---
  ServiceTaskList: 'service_tasks',
  ServiceTaskAllList: 'service_tasks',
  PreviewServiceTask: 'service_tasks.preview',
  ServiceTaskAdd: 'service_tasks',
  TaskTemplateList: 'service_task.templates',
  ServiceUsersSettings: 'service_task.access_levels',
  ServiceStateList: 'service_task.states',
  ServiceGeneralSettings: 'service_task.setup',
  CustomerLevelsSettings: 'crm.levels',
  CustomerStatesList: 'crm.states',
  CustomerStatesAdd: 'crm.states',
  CustomerTradersList: 'crm.traders',
  CustomerTradersAdd: 'crm.traders',
  CustomerProductionQuality: 'crm.production_quality',
  CustomerSalesEvaluation: 'crm.sales_evaluation',
  CustomerMyCustomersList: 'crm.sales_evaluation',
  CustomerMyCustomersDetail: 'crm.sales_evaluation',
  CustomerAllPartnersList: 'crm.sales_evaluation',
  CustomerAllPartnersDetail: 'crm.sales_evaluation',
  CustomerMyBusinessByCustomerList: 'crm.sales_evaluation',
  CustomerMyBusinessByCustomerDetail: 'crm.sales_evaluation',
  CustomerMyBusinessByStateList: 'crm.communication',
  CrmCommunicationNewTaskHub: 'crm.communication',
  CrmCommunicationTicketsList: 'crm.communication',
  CrmCommunicationMyAgendaList: 'crm.communication',
  CrmCommunicationCompleteList: 'crm.communication',
  CrmCommunicationTicketDetail: 'crm.communication',
  PreviewCrmCommunication: 'crm.communication',
  MaintenanceTaskRecordDetail: 'service_tasks',
  MaintenanceNeedRecordDetail: 'service_tasks',
  MaintenanceTaskStateList: 'service_tasks',
  MaintenanceNeedStateList: 'service_tasks',
  MaintenanceNeedRecordList: 'service_tasks',
  MaintenanceTaskRecordList: 'service_tasks',
  PreviewMaintenance: 'maintenance.preview',
  MaintenanceRecordAdd: 'maintenance.machine_cards',

  MaintenanceMachineStateList: 'maintenance.machine_states',
  MaintenanceMachineStateDetail: 'maintenance.machine_states',
  MaintenanceCheckStateList: 'maintenance.check_records',
  MaintenanceCheckRecordList: 'maintenance.check_records',
  MaintenanceCheckRecordDetail: 'maintenance.check_records',
  MaintenanceMachineList: 'maintenance.machines',
  MaintenanceMachineDetail: 'maintenance.machines',

  MaintenanceTemplateList: 'maintenance.templates',
  MaintenanceTemplateRecordList: 'maintenance.templates',
  MaintenanceTemplateRecordAdd: 'maintenance.templates',
  maintenance_templates: 'maintenance.templates',

  MaintenanceCompanyCardList: 'maintenance.companies',
  MaintenanceCompanyContactList: 'maintenance.companies',
  MaintenanceCompanyCertificateList: 'maintenance.companies',
  MaintenanceCompanyCertRecordList: 'maintenance.companies',
  MaintenanceCompanyCertStateList: 'maintenance.companies',
  MaintenanceCompanyCertRecordDetail: 'maintenance.companies',
  MaintenanceCompanyContactAdd: 'maintenance.companies',
  MaintenanceCompanyCertificateAdd: 'maintenance.companies',
  MaintenanceCompanyCardDetail: 'maintenance.companies',
  MaintenanceCompanyCertRecordAdd: 'maintenance.companies',
  SummaryEmailMaintenanceList: 'maintenance.summary_emails',
  MaintenanceGeneralSettings: 'maintenance.setup',

  PriorityList: 'priorities.list',
  PriorityListMobile: 'priorities.list',
  PriorityAdd: 'priorities.settings',
  PriorityDepartmentList: 'priorities.settings',
  PriorityDepartmentAdd: 'priorities.settings',
  PriorityLoadAutomaticList: 'priorities.settings',
  PriorityLoadManuallyList: 'priorities.settings',
  PriorityLoadAdd: 'priorities.settings',
  priority: 'priorities.settings',

  InternaChyba: 'otk.internal_errors',
  ExternaChyba: 'otk.external_errors',
  Reklamacia: 'otk.complaints',
  // OTK – zoznamy z menu (rovnaké topic ako príslušný formulár / tabuľka chýb)
  ZoznamInt: 'otk.internal_errors',
  ZoznamExt: 'otk.external_errors',
  ZoznamRek: 'otk.complaints',
  PreviewOTK: 'otk.preview',
  // Fotodokumentácia: jedna entita meraní, filtrovaná podľa typu obrazovky
  Documentation: 'otk.documentation',
  MeasurementProtocol: 'otk.documentation',
  SelfControl: 'otk.documentation',
  NDTProtocol: 'otk.documentation',
  AnnealingProtocol: 'otk.documentation',
  DocumentationAdd: 'otk.documentation',
  DocumentationDetail: 'otk.documentation',
  OTKEvaluation: 'otk.statistics',
  SummaryEmailOTKList: 'summary_email_otk',
  OtkGeneralSettings: 'otk.setup',
  OtkStatisticsSettings: 'otk.setup',
  OtkCompanyCertRecordAdd: 'otk_company_certificate',

  ChecklistRecordAllList: 'checklist_templates',
  ChecklistRecordMyList: 'checklist_templates',
  ChecklistStateList: 'checklist_templates',
  ChecklistRecordList: 'checklist_templates',
  ChecklistTemplateList: 'checklist_templates',
  ChecklistRecordAdd: 'checklist_records',
  SummaryEmailChecklistList: 'summary_email_checklists',
  PreviewChecklists: 'checklist.preview',
  ChecklistGeneralSettings: 'checklist.setup',

  EmployeeList: 'settings.employees',
  DepartmentList: 'settings.departments',
  TrainingRecordList: 'training_templates',
  TrainingTemplateList: 'training_templates',
  TrainingTemplateRecordList: 'training_templates',
  TrainingRecordDetail: 'training_cards',
  TrainingTemplateRecordAdd: 'training_cards',
  TrainingCompanyContactAdd: 'training_cards',
  TrainingEmployeeList: 'training.lists',
  TrainingStateList: 'training.lists',
  TrainingCompanyCertStateList: 'training.lists',
  CalibrationCompanyCertStateList: 'training.lists',
  TrainingEmployeeDetail: 'training_employees',
  TrainingInvitationList: 'training_invitations',
  TrainingInvitationTemplateList: 'training_invitation_templates',
  SummaryEmailTrainingList: 'gauge_calibrations',
  TrainingCompanyCertRecordDetail: 'gauge_calibrations',
  TrainingCompanyCertificateList: 'calibration_company_certificates',
  TrainingCompanyCertRecordList: 'training_company_certificates',
  TrainingCompanyList: 'training_companies',
  TrainingCompanyCardList: 'training_companies',
  TrainingCompanyCardDetail: 'training_companies',
  TrainingCompanyContactList: 'training_companies',
  PreviewTraining: 'training.preview',
  TrainingCardList: 'training.employee_cards',
  TrainingCardAdd: 'training.employee_cards',
  TrainingCardDetail: 'training.employee_cards',
  TrainingTypeList: 'training.type_definitions',
  TrainingTypeAdd: 'training.type_definitions',
  TrainingRecordAdd: 'training_templates',
  TrainingGeneralSettings: 'training.setup',
  LecturerList: 'training.lecturers',
  TrainingCompanyCertificateAdd: 'training_company_certificates',
  TrainingCompanyAdd: 'training_companies',
  TrainingCompanyCertRecordAdd: 'training_company_certificates',

  GaugeCalibrationList: 'gauge_calibrations',
  GaugeCalibrationAdd: 'gauge_calibrations',
  SummaryEmailCalibrationList: 'gauge_calibrations',
  CalibrationCompanyCertRecordDetail: 'gauge_calibrations',
  CalibrationCompanyCertRecordList: 'calibration_company_certificates',
  CalibrationCompanyCertificateList: 'calibration_company_certificates',
  CalibrationCompanyCertificateAdd: 'companies',
  CalibrationCompanyCardList: 'calibration_company_certificates',
  CalibrationCompanyCardDetail: 'companies',

  OtkCompanyCertRecordDetail: 'otk_company_certificates',
  OtkCompanyCertificateList: 'otk_company_certificates',
  OtkCompanyCardList: 'otk_company_certificates',
  OtkCompanyCertRecordList: 'calibration_company_certificates',
  OtkCompanyCertStateList: 'otk_company_cert_state',
  OtkCompanyCertificateAdd: 'otk_company_certificate',
  OtkCompanyCardDetail: 'otk_company_certificate',
  OtkCompanyStatisticsList: 'otk_company_statistics',
  OtkCompanyStatisticsPeriodList: 'otk_company_statistics',
  OtkCompanyStatisticsAdd: 'otk_company_statistics',

  MeasureComplexityList: 'otk.internal_errors',
  ProtocolTypeList: 'otk.internal_errors',
  GroupList: 'otk.internal_errors',
  PreventionList: 'otk.internal_errors',
  ErrorTypeList: 'otk.internal_errors',
  RootCauseList: 'root_causes',

  OTKStatistics: 'otk.statistics',
  SummaryEmailPaintingList: 'summary_email_painting',
  WeatherList: 'painting.weather',
  PaintingIntervalList: 'painting.intervals',
  PaintingEmployeeConditionList: 'painting.employee_conditions',
  PaintingTimeConditionList: 'painting.time_conditions',
  PreviewLak: 'painting.preview',
  PaintingConditionList: 'painting.conditions',
  PaintingConditionAdd: 'painting.conditions',

  PreviewCal: 'calibration.preview',
  GaugeList: 'calibration.gauges',
  CalibrationStateList: 'gauge_calibrations',
  CalibrationGeneralSettings: 'calibration.setup',
  CalibrationCompanyCertRecordAdd: 'calibration_company_certificates',

  MachineList: 'machines',
  RoleList: 'roles',
  MachineTypeList: 'machine_types',
  UserList: 'users',
  BusinessPartnerList: 'settings.business_partners',
  BusinessPartnerAdd: 'settings.business_partners',
  CustomerList: 'settings.customers',
  CustomerAdd: 'settings.customers',
  SupplierList: 'settings.suppliers',
  SupplierAdd: 'settings.suppliers',
  UnitsSettings: 'settings.units',
  CompanySettings: 'settings.company_profile',
  ChecklistHolidayList: 'settings.checklist_holidays',
  ChecklistHolidayAdd: 'settings.checklist_holidays',
  DepartmentAdd: 'settings.departments',
  EmployeeAdd: 'settings.employees',
  UserAdd: 'users',
  RoleAdd: 'roles',
  MachineAdd: 'machines',
  MachineTypeAdd: 'machine_types',

  SupportTicketList: 'support_tickets',
  SupportTicketDetail: 'support_tickets',
  SupportTicketAdd: 'support_tickets',

  MaintenanceTypeAdd: 'maintenance.types',
  MaintenanceTemplateAdd: 'maintenance.templates',
  MaintenanceCardAdd: 'maintenance.machine_cards',

  ErrorTypeAdd: 'otk.internal_errors',
  GroupAdd: 'otk.internal_errors',
  PreventionAdd: 'otk.internal_errors',
  MeasureComplexityAdd: 'otk.internal_errors',
  ProtocolTypeAdd: 'otk.internal_errors',
  RootCauseAdd: 'root_causes',
  OtkCompanyStatisticsPeriodAdd: 'otk_company_statistics',

  SummaryEmailChecklistAdd: 'summary_email_checklists',
  SummaryEmailTrainingAdd: 'gauge_calibrations',
  SummaryEmailCalibrationAdd: 'gauge_calibrations',
  SummaryEmailMaintenanceAdd: 'maintenance.summary_emails',
  SummaryEmailOTKAdd: 'summary_email_otk',
  SummaryEmailPaintingAdd: 'summary_email_painting',

  ChecklistTemplateAdd: 'checklist_templates',
  ChecklistRecordMyDetail: 'checklist_records',
  ChecklistRecordAllDetail: 'checklist_records',
  ChecklistStateDetail: 'checklist_records',

  TrainingTemplateAdd: 'training_templates',
  TrainingInvitationAdd: 'training_invitations',
  TrainingInvitationTemplateAdd: 'training_invitation_templates',
  LecturerAdd: 'training.lecturers',

  GaugeAdd: 'calibration.gauges',
  PaintingEmployeeConditionAdd: 'painting.employee_conditions',
  PaintingIntervalAdd: 'painting.intervals',
  PaintingTimeConditionAdd: 'painting.time_conditions',
  WeatherAdd: 'painting.weather',

  ServiceTaskDetail: 'service_tasks',
  ServiceStateAdd: 'service_task.states',
  TaskTemplateAdd: 'service_task.templates',
};

const LEGACY_CHOSEN_STRING_COLLAPSE = {
  training_types: 'maintenance.companies',
  gauge_calibrations: 'maintenance.companies',
  maintenance_companiess: 'maintenance.companies',
  maintenance_company_certificate: 'maintenance.companies',
  maintenance_company_certificates: 'maintenance.companies',
  calibration_company_certificates: 'maintenance.companies',
  machine_cards: 'maintenance.check_records',
  'maintenance.task_records': 'service_tasks',
  'maintenance.need_records': 'service_tasks',
  maintenance_task_records: 'service_tasks',
  maintenance_need_records: 'service_tasks',
  'maintenance.faults_and_purchases': 'service_tasks',
};

/** Historicky jeden `otk_int` ukladali štatistiky OTK, emaily lakovne a nastavenia lakovne – pri čítaní preferencií ich spájame. */
const LEGACY_OTK_INT_SUCCESSOR_TOPICS = [
  'otk.statistics',
  'summary_email_painting',
  'painting.weather',
  'painting.intervals',
  'painting.employee_conditions',
  'painting.time_conditions',
];

const ALLOWED_TOPICS = new Set([
  DEFAULT_TOPIC,
  ...Object.values(PAGE_TO_TOPIC),
  ...Object.keys(LEGACY_CHOSEN_STRING_COLLAPSE),
  ...LEGACY_OTK_INT_SUCCESSOR_TOPICS,
]);

/** Oddelenia a zamestnanci boli pod `training_templates` so školeniami – pri čítaní DB ešte hľadáme starý typ. */
const PAGES_LEGACY_TRAINING_TEMPLATES_GLOB = new Set(['DepartmentList', 'DepartmentAdd', 'EmployeeList', 'EmployeeAdd']);

export function resolveNotificationType(pageOrType) {
  const key = String(pageOrType ?? '').trim();
  if (!key) return DEFAULT_TOPIC;
  const mapped = PAGE_TO_TOPIC[key] ?? key;
  return ALLOWED_TOPICS.has(mapped) ? mapped : DEFAULT_TOPIC;
}

export function collapseChosenStoredType(pageOrType) {
  let s = String(pageOrType ?? '').trim();
  if (!s) return DEFAULT_TOPIC;
  s = resolveNotificationType(s);
  for (let i = 0; i < 8; i += 1) {
    const next = LEGACY_CHOSEN_STRING_COLLAPSE[s];
    if (!next || next === s) break;
    s = next;
  }
  return s;
}

export function getNotificationLookupTypes(page) {
  const raw = String(page ?? '').trim();
  if (!raw) return [];
  const canonical = collapseChosenStoredType(raw);
  const resolved = resolveNotificationType(raw);
  const out = new Set([raw, resolved, canonical].filter(Boolean));

  for (const k of Object.keys(PAGE_TO_TOPIC)) {
    if (collapseChosenStoredType(k) === canonical) {
      out.add(k);
      const v = PAGE_TO_TOPIC[k];
      if (v) out.add(v);
    }
  }
  for (const leg of Object.keys(LEGACY_CHOSEN_STRING_COLLAPSE)) {
    if (collapseChosenStoredType(leg) === canonical) out.add(leg);
  }

  if (canonical === 'otk_int' || resolved === 'otk_int' || raw === 'otk_int') {
    out.add('otk_int');
    for (const t of LEGACY_OTK_INT_SUCCESSOR_TOPICS) out.add(t);
  }
  for (const t of LEGACY_OTK_INT_SUCCESSOR_TOPICS) {
    if (canonical === t || resolved === t) {
      out.add('otk_int');
      break;
    }
  }

  if (PAGES_LEGACY_TRAINING_TEMPLATES_GLOB.has(raw)) {
    out.add('training_templates');
  }
  if (canonical === 'settings.departments' || canonical === 'settings.employees') {
    out.add('training_templates');
  }

  return Array.from(out);
}

export function getMaintenanceNotificationTopicMap() {
  return { ...PAGE_TO_TOPIC };
}

export function getNotificationTopicDisplay(typeOrPage, t) {
  const resolvedTopic = collapseChosenStoredType(typeOrPage);
  const portalKey = PORTAL_SERVICE_NOTIFICATION_LABEL_KEYS[resolvedTopic];
  if (portalKey && typeof t === 'function') {
    return { topic: resolvedTopic, label: t(portalKey) };
  }
  const staticLabel = NOTIFICATION_TOPIC_DISPLAY_LABEL[resolvedTopic];
  if (staticLabel) {
    return {
      topic: resolvedTopic,
      label: typeof t === 'function' ? t(staticLabel) : staticLabel,
    };
  }
  return {
    topic: resolvedTopic,
    label: typeof t === 'function' ? t('NotificationTopic.Default') : t('Common.Notifications'),
  };
}
